import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboards.css';
import { API_BASE_URL } from '../config.js';
import api from '../api/index.js';
import TeamCalendar from './TeamCalendar.js';
import { useToast } from '../context/ToastContext.js';

const adminMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const QUICK_ACTIONS = [
  { icon: '👤', label: 'Roster', to: '/athletes' },
  { icon: '📝', label: 'Billing', to: '/billing' },
  { icon: '📚', label: 'Classes', to: '/classes-hub' },
  { icon: '📞', label: 'Message', to: '/communication' },
  { icon: '👪', label: 'Parents', to: '/admin/parents' },
  { icon: '👤', label: 'Users', to: '/admin/users' },
];

// ─── Today at a Glance ────────────────────────────────────────
function TodayAtAGlance() {
  const navigate = useNavigate();
  const [todayClasses, setTodayClasses] = useState([]);
  const [overdueCount, setOverdueCount] = useState(0);
  const [nextMeet, setNextMeet] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = (() => { try { return JSON.parse(localStorage.getItem('user'))?.token; } catch { return null; } })();

  useEffect(() => {
    const fetchGlanceData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };

        const [classRes, invoiceRes] = await Promise.allSettled([
          fetch(`${API_BASE_URL}/api/classes?active_only=true`, { headers }),
          fetch(`${API_BASE_URL}/api/invoices`, { headers }),
        ]);

        if (classRes.status === 'fulfilled' && classRes.value.ok) {
          const d = await classRes.value.json();
          setTodayClasses(Array.isArray(d) ? d : []);
        }
        if (invoiceRes.status === 'fulfilled' && invoiceRes.value.ok) {
          const d = await invoiceRes.value.json();
          const overdue = Array.isArray(d) ? d.filter(inv => inv.status === 'overdue') : [];
          setOverdueCount(overdue.length);
        }

        // Optionally fetch next meet from calendar
        try {
          const meetRes = await fetch(
            `${API_BASE_URL}/api/calendar/events?event_type=competition&upcoming=true&limit=1`,
            { headers }
          );
          if (meetRes.ok) {
            const events = await meetRes.json();
            if (Array.isArray(events) && events.length > 0) setNextMeet(events[0]);
          }
        } catch { /* calendar endpoint may not exist yet — safe to ignore */ }
      } catch (err) {
        console.error('TodayAtAGlance fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchGlanceData();
  }, [token]);

  const daysUntil = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    return Math.max(0, Math.ceil((d - now) / (1000 * 60 * 60 * 24)));
  };

  return (
    <div className="today-glance-panel">
      <h2 className="today-glance-title">
        ⚡ Today at a Glance
        <span className="today-date">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </span>
      </h2>
      <div className="today-glance-grid">
        <div className="glance-card glance-blue" onClick={() => navigate('/attendance')}>
          <div className="glance-icon">📚</div>
          <div className="glance-value">{loading ? '—' : todayClasses.length}</div>
          <div className="glance-label">Active Classes</div>
          <div className="glance-action">Take Attendance →</div>
        </div>

        <div
          className={`glance-card ${overdueCount > 0 ? 'glance-red' : 'glance-green'}`}
          onClick={() => navigate('/invoices')}
        >
          <div className="glance-icon">{overdueCount > 0 ? '⚠️' : '✅'}</div>
          <div className="glance-value">{loading ? '—' : overdueCount}</div>
          <div className="glance-label">Overdue Invoices</div>
          <div className="glance-action">{overdueCount > 0 ? 'View & collect →' : 'All paid up!'}</div>
        </div>

        <div className="glance-card glance-purple" onClick={() => navigate('/meet-coordination')}>
          <div className="glance-icon">🏆</div>
          <div className="glance-value">{loading ? '—' : nextMeet ? `${daysUntil(nextMeet.start_time)}d` : '—'}</div>
          <div className="glance-label">Next Meet</div>
          <div className="glance-action">{nextMeet ? nextMeet.title : 'No meets scheduled →'}</div>
        </div>

        <div className="glance-card glance-orange" onClick={() => navigate('/communication')}>
          <div className="glance-icon">💬</div>
          <div className="glance-value">Send</div>
          <div className="glance-label">Team Message</div>
          <div className="glance-action">Open Communication →</div>
        </div>
      </div>
    </div>
  );
}

/**
 * ADMIN DASHBOARD
 * Full control within their single team
 */
function AdminDashboard({ user, dashboardData }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(dashboardData?.summary || {});
  const [pendingCoaches, setPendingCoaches] = useState(dashboardData?.pendingCoaches || []);
  const [invoiceSummary, setInvoiceSummary] = useState({});
  const [waiverAlerts, setWaiverAlerts] = useState({});
  const [activeTab, setActiveTab] = useState('overview');

  // Load the real overview counts (athletes/classes/coaches/parents) — the
  // parent passes an empty dashboardData, so the cards must fetch their own data.
  // Uses the shared `api` instance (per-request auth — no first-paint race).
  useEffect(() => {
    api.get(`${API_BASE_URL}/api/admin/dashboard`)
      .then(res => {
        setSummary(res.data?.summary || {});
        setPendingCoaches(res.data?.pendingCoaches || []);
        setInvoiceSummary(res.data?.invoiceSummary || {});
        setWaiverAlerts(res.data?.waiverAlerts || {});
      })
      .catch(err => console.error('Admin dashboard load error:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approveCoach = async (coachId) => {
    try {
      await api.put(`${API_BASE_URL}/api/auth/approve-coach/${coachId}`, {});
      
      toast.success('Coach approved successfully!');
      setPendingCoaches(pendingCoaches.filter(c => c.id !== coachId));
    } catch (err) {
      console.error('Error approving coach:', err);
      toast.error('Failed to approve coach');
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>🏢 Admin Dashboard</h1>
        <div className="user-info">
          <span>{user.name}</span>
          <span className="team-badge">{user.teamName}</span>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
          Overview
        </button>
        <button className={`tab ${activeTab === 'approvals' ? 'active' : ''}`} onClick={() => setActiveTab('approvals')}>
          Approvals {pendingCoaches.length > 0 && <span className="badge-count">{pendingCoaches.length}</span>}
        </button>
        <button className={`tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
          Settings
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="overview-section">
          <div className="stats-grid">
            <div className="stat-card blue">
              <div className="stat-value">{summary.total_athletes || 0}</div>
              <div className="stat-label">Total Athletes</div>
            </div>
            <div className="stat-card green">
              <div className="stat-value">{summary.total_classes || 0}</div>
              <div className="stat-label">Active Classes</div>
            </div>
            <div className="stat-card orange">
              <div className="stat-value">{summary.total_coaches || 0}</div>
              <div className="stat-label">Coaches</div>
            </div>
            <div className="stat-card purple">
              <div className="stat-value">{summary.total_parents || 0}</div>
              <div className="stat-label">Parents</div>
            </div>
          </div>

          {/* Quick Actions (folded in from the old /admin/dashboard) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(108px, 1fr))', gap: 10, margin: '1.25rem 0' }}>
            {QUICK_ACTIONS.map(a => (
              <button key={a.to} onClick={() => navigate(a.to)}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, padding: '14px 8px', border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', cursor: 'pointer' }}>
                <span style={{ fontSize: '1.4rem' }}>{a.icon}</span>
                <span style={{ fontSize: '.82rem', fontWeight: 600, color: '#374151' }}>{a.label}</span>
              </button>
            ))}
          </div>

          {/* Alerts (folded in from the old /admin/dashboard) */}
          {(() => {
            const overdue = Number(invoiceSummary.overdue_count || 0);
            const waivers = Number(waiverAlerts.expiring_waivers || 0);
            const approvals = pendingCoaches.length;
            if (!overdue && !waivers && !approvals) return null;
            const chip = (bg, color, text, key) => (
              <div key={key} style={{ background: bg, color, borderRadius: 10, padding: '10px 14px', fontSize: '.88rem', fontWeight: 600 }}>{text}</div>
            );
            return (
              <div style={{ display: 'grid', gap: 8, margin: '0 0 1.25rem' }}>
                {overdue > 0 && chip('#fef2f2', '#991b1b', `🧾 ${overdue} overdue invoice(s) · ${adminMoney(invoiceSummary.outstanding)} outstanding`, 'o')}
                {waivers > 0 && chip('#fffbeb', '#92400e', `📄 ${waivers} waiver(s) pending, missing, or expiring`, 'w')}
                {approvals > 0 && chip('#eff6ff', '#1e40af', `🧑‍🏫 ${approvals} coach account(s) awaiting approval`, 'a')}
              </div>
            );
          })()}

          <TodayAtAGlance />

          <div className="dashboard-calendar-section">
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem', marginTop: '1.5rem' }}>📅 Club Master Calendar</h2>
            <TeamCalendar />
          </div>

          <div className="permissions-info card">
            <h3>Your Admin Permissions</h3>
            <div className="permissions-grid">
              <div className="permission-item">✅ Manage waivers & insurance</div>
              <div className="permission-item">✅ Set tuition prices</div>
              <div className="permission-item">✅ Create payment plans</div>
              <div className="permission-item">✅ Manage discount codes</div>
              <div className="permission-item">✅ Approve coach accounts</div>
              <div className="permission-item">✅ View all athletes in team</div>
              <div className="permission-item">✅ Manage class schedules</div>
              <div className="permission-item">✅ Export reports</div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'approvals' && (
        <div className="approvals-section">
          <h2>Pending Coach Approvals</h2>
          
          {pendingCoaches.length === 0 ? (
            <p className="empty-state">No pending coach approvals</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Applied</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingCoaches.map(coach => (
                  <tr key={coach.id}>
                    <td>{coach.name}</td>
                    <td>{coach.email}</td>
                    <td>{new Date(coach.created_at).toLocaleDateString()}</td>
                    <td>
                      <button 
                        className="btn btn-success btn-small"
                        onClick={() => approveCoach(coach.id)}
                      >
                        Approve
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="settings-section">
          <h2>Team Settings</h2>
          <div className="card">
            <p><strong>Team:</strong> {user.teamName}</p>
            <p><strong>Team ID:</strong> {user.teamId}</p>
            <p className="info-text">
              ℹ️ As an admin, you have full control within your team. You cannot access data from other teams.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * COACH DASHBOARD
 * Athlete-facing operations and scheduling
 */
function CoachDashboard({ user, dashboardData }) {
  const navigate = useNavigate();
  const [classes, setClasses] = useState(dashboardData?.assignedClasses || []);
  const [athleteCount, setAthleteCount] = useState(dashboardData?.athleteCount || 0);

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>🏋️ Coach Dashboard</h1>
        <div className="user-info">
          <span>{user.name}</span>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card blue">
          <div className="stat-value">{classes.length}</div>
          <div className="stat-label">Assigned Classes</div>
        </div>
        <div className="stat-card green">
          <div className="stat-value">{athleteCount}</div>
          <div className="stat-label">Total Athletes</div>
        </div>
      </div>

      <TodayAtAGlance />

      <div className="dashboard-calendar-section">
        <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem', marginTop: '1.5rem' }}>📅 Club Calendar</h2>
        <TeamCalendar />
      </div>
    </div>
  );
}

/**
 * PARENT DASHBOARD
 * View all children and manage payments
 */
function ParentDashboard({ user, dashboardData }) {
  const navigate = useNavigate();
  const [children, setChildren] = useState(dashboardData?.children || []);
  const [upcomingPayments, setUpcomingPayments] = useState(dashboardData?.upcomingPayments || []);

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>👨‍👩‍👧‍👦 Parent Dashboard</h1>
        <div className="user-info">
          <span>{user.name}</span>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card blue">
          <div className="stat-value">{children.length}</div>
          <div className="stat-label">Your Children</div>
        </div>
        <div className="stat-card orange">
          <div className="stat-value">{upcomingPayments.length}</div>
          <div className="stat-label">Pending Payments</div>
        </div>
      </div>

      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="action-buttons">
          <button className="action-btn" onClick={() => navigate('/parent/invoices')}>
            💳 Make Payment
          </button>
          <button className="action-btn" onClick={() => navigate('/waivers')}>
            📝 Sign Waivers
          </button>
          <button className="action-btn" onClick={() => navigate('/communication')}>
            💬 Message Coaches
          </button>
          <button className="action-btn" onClick={() => navigate('/calendar')}>
            📅 View Schedule
          </button>
        </div>
      </div>

      <div className="card">
        <h2>Your Children</h2>
        {children.length === 0 ? (
          <div className="empty-state">
            <p>No children linked yet</p>
            <button className="btn btn-primary" onClick={() => navigate('/parent/athletes')}>
              Link Children
            </button>
          </div>
        ) : (
          <div className="children-grid">
            {children.map(child => (
              <div key={child.athlete_id} className="child-card">
                <h3>{child.athlete_name}</h3>
                <p>Team: {child.team_name}</p>
                <p>DOB: {new Date(child.athlete_dob).toLocaleDateString()}</p>
                <p className={`relationship-badge ${child.is_primary ? 'primary' : ''}`}>
                  {child.relationship} {child.is_primary && '(Primary)'}
                </p>
                <button
                  className="btn btn-small btn-primary"
                  onClick={() => navigate(`/athlete-card/${child.athlete_id}`)}
                >
                  View Details
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {upcomingPayments.length > 0 && (
        <div className="card">
          <h2>Upcoming Payments</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>Athlete</th>
                <th>Amount</th>
                <th>Due Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {upcomingPayments.map(payment => (
                <tr key={payment.id}>
                  <td>{payment.athlete_name}</td>
                  <td>${payment.amount}</td>
                  <td>{new Date(payment.due_date).toLocaleDateString()}</td>
                  <td>
                    <button className="btn btn-small btn-success" onClick={() => navigate('/parent/invoices')}>Pay Now</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * ATHLETE DASHBOARD
 * View only their own data
 */
function AthleteDashboard({ user, dashboardData }) {
  const navigate = useNavigate();
  const [athleteData, setAthleteData] = useState(dashboardData?.athlete || {});
  const [recentVideos, setRecentVideos] = useState(dashboardData?.recentVideos || []);

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>🤸 Athlete Dashboard</h1>
        <div className="user-info">
          <span>{user.name}</span>
        </div>
      </div>

      <div className="athlete-profile card">
        <h2>My Profile</h2>
        <div className="profile-info">
          <p><strong>Name:</strong> {athleteData.athlete_name || user.name}</p>
          <p><strong>Team:</strong> {athleteData.team_name || user.teamName}</p>
          {athleteData.athlete_dob && (
            <p><strong>DOB:</strong> {new Date(athleteData.athlete_dob).toLocaleDateString()}</p>
          )}
        </div>
      </div>

      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="action-buttons">
          <button className="action-btn" onClick={() => navigate('/stats')}>
            📊 My Stats &amp; Videos
          </button>
          <button className="action-btn" onClick={() => navigate('/calendar')}>
            📅 My Schedule
          </button>
          <button className="action-btn" onClick={() => navigate('/communication')}>
            💬 Message Coaches
          </button>
          <button className="action-btn" onClick={() => navigate('/profile')}>
            👤 My Profile
          </button>
        </div>
      </div>

      {recentVideos.length > 0 && (
        <div className="card">
          <h2>Recent Videos</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>Video</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentVideos.map(video => (
                <tr key={video.id}>
                  <td>{video.file_name}</td>
                  <td>{new Date(video.created_at).toLocaleDateString()}</td>
                  <td>
                    <span className={`badge badge-${
                      video.analysis_status === 'completed' ? 'green' :
                      video.analysis_status === 'processing' ? 'blue' :
                      video.analysis_status === 'failed' ? 'red' : 'gray'
                    }`}>
                      {video.analysis_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export { AdminDashboard, CoachDashboard, ParentDashboard, AthleteDashboard };
